/**
 * Description: 输入面板
 * User: 常胤 <satans17@gmail.com>
 * Date: 16/01/23
 * Copyright(c) 2016 Taobao.com
 *  update:
 *    2016-01-25 changyin
 *      * 剥离所有和业务相关的代码
 */

import events from 'events';
import $ from '@ali/tarzan-zepto';
import inputPanelTemplate from '../../baseui/index.js';
import Input from './input/index';
import util from '../../../lib/util';
import { Env, windvane } from '../../../utils';
import InputTip from './inputtip/index';
import { smileytip } from './smileytip/index';
import { AGENT, AGENT_MOD, DESKTOP_MOD, MOBILE_MOD, ACTIVED_MOD, BLOCKED_MOD } from '../../../const/objects.const';

require('./alime-footer.scss');

const EMOJI_REG = /\ud83c[\udf00-\udfff]|\ud83d[\udc00-\ude4f]|\ud83d[\ude80-\udeff]/gim;
const LOG_C = 'inputpanel';

const isPC = util.isPC();

export default class InputPanel extends events {

  constructor(config) {
    super();
    /**
     * user config
     */
    this.config = config;
    /**
     * root dom
     */
    this.$container = $(config.container);
    /**
     * 埋点
     */
    this.log = config.log;

    /**
     * 插件面板
     * @type {null}
     */
    this.$plugin = null;

    /**
     * 快捷短语
     */
    this.$shortcuts = null;

    this.$endConversationHolder = null;
    /**
     * 输入对象，语音&文本
     */
    this.input = null;

    /**
     * 输入提示对象
     * @type {null}
     */
    this.inputTip = null;

    this.elementSelectors = {
      sendButtonId: '#alime-send-button',
      emojiOpenerId: '#alime-emoji-opener',
      sendArrowId: '#alime-send-arrow',
      pluginToggle: '#csc-plugin-toggle',
    };

    this.__render();
    this.__bindEvent();
    this.__hack();
  }

  /**
   * 渲染
   * @private
   */
  __render() {
    const $container = this.$container;
    const { sendButtonId, emojiOpenerId, sendArrowId, pluginToggle } = this.elementSelectors;

    $container
      .html(inputPanelTemplate.frame({ type: 'footer' }))
      .addClass(isPC ? DESKTOP_MOD : MOBILE_MOD);

    // 缓存常用对象
    this.$plugin = $container.find('#alime-plugins-wrapper');
    this.$shortcuts = $container.find('#alime-shortcuts-wrapper');
    this.$endConversationHolder = $('#end-conversation-holder');

    this.input = this.__initInput();

    this.$sendShortcut = $container.find('.J_OrderSelect');
    this.$sendShortcutInner = $container.find('.J_Orderlist');
    // this.$pluginTrigger = $container.find('.J_ShowPluginBoard');
    this.$pluginTrigger = $container.find(pluginToggle);
    this.$inputBtn = $container.find(sendButtonId);
    this.$emojiBtn = $container.find(emojiOpenerId);
    this.$sendArrow = $container.find(sendArrowId);

    if (Env.isWebview) {
      $container.find('.csc-upload-button._input').remove();
      $container.find('.csc-camera-button._input').remove();
    }

    if (isPC) {
      $container.find('.csc-camera-button._block').parent().remove();
    } else {
      $container.find('.csc-attach-button._block').parent().remove();
    }
  }

  /**
   * 提交数据
   * @param data 数据
   * @param type 数据类型  connect order imgTag
   * @param show 是否显示
   * @param post 是否提交
   */
  submit(data, type = 'text', show = true, post = true) {
    let convertData = data;
    // 如果需要发送到后端的emoji表情,替换成[表情]文字
    if (post && util.isString(convertData) && EMOJI_REG.test(convertData)) {
      convertData = data.replace(EMOJI_REG, '[\u8868\u60C5]');
    }
    if (post && util.isObject(convertData) && EMOJI_REG.test(convertData.q)) {
      convertData.q = convertData.q.replace(EMOJI_REG, '[\u8868\u60C5]');
    }
    this.emit('message', [convertData, type, show, post]);
    // 发送消息之后,需要滚动到最底部
    if (show) {
      this.scrollToBottom();
    }
  }

  focus() {
    const $container = this.$container;
    if (this.input.field.inputFormat === 'number') {
      $container.find('.J_NumberField').trigger('focus');
    } else {
      $container.find('.J_Field').trigger('focus');
    }
  }

  blur() {
    const $container = this.$container;
    if (this.input.field.inputFormat === 'number') {
      $container.find('.J_NumberField').trigger('blur');
    } else {
      $container.find('.J_Field').trigger('blur');
    }
    this.__hackFocus();
  }

  __hackFocus() {
    const $$container = $('.m-alicare-body');

    // 解决focus到输入框后不能失去焦点的问题
    $$container.attr('tabindex', 1);
    // 进页面第一次点击不生效,应该是第一次没有focus,机智的我,进页面先手动触发下focus,红红火火恍恍惚惚
    $$container.trigger('focus');
  }

  /**
   * 设置输入提示文本
   */
  setTipText(text) {
    this.inputTip = new InputTip(text, this.$container);
    this.inputTip.on('send', (ev) => {
      this.submit(ev.text);
    });
  }

  setPlaceholder(text) {
    this.input.field.$field.attr('placeholder', text);
  }

  __bindEvent() {
    const $container = this.$container;
    const pressEvent = isPC ? 'click' : 'touchstart';
    this.$inputBtn.on(pressEvent, (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const hasActiveSendBtn = this.$sendArrow.hasClass(ACTIVED_MOD);
      if (hasActiveSendBtn) {
        this.__sendTextMessage();
        this.input.field.adjustTextareaHeight();
      }
    });

    $container.find('.J_Field').on('input', (ev) => {
      const $target = $(ev.currentTarget);
      $(document).trigger('inputing');
      smileytip($target.val(), (text) => {
        this.submit(text, 'liveImage');
        $target.val('');
        $target.trigger('change');
      });
    });

    $container.find('.J_Field,.J_NumberField').on('keydown keyup', (ev) => {
      if (isPC) {
        const f = this.input.field;
        const val = f.getValue() || '';
        const isVal = /([^\s])/.test(val);

        if (
            ($container.hasClass(AGENT_MOD) && $container.hasClass(BLOCKED_MOD)) ||
            (ev.keyCode === 13 && !isVal)
         ) {
          ev.preventDefault();
        }
        if (ev.keyCode === 13 && !ev.shiftKey && isVal) {
          ev.preventDefault();
          if (!$container.hasClass(AGENT_MOD) || !$container.hasClass(BLOCKED_MOD)) {
            this.__sendTextMessage();
            this.input.field.adjustTextareaHeight();
            this.log.send({
              c: LOG_C,
              d: 'enter',
            });
          }
        }
      }
    });
    $container.find('.J_Field,.J_NumberField').on('focus', () => {
      if (this.inputTip) {
        this.inputTip.showTip();
      }
      this.emit('focus');
      this.__scrollIntoView();
    });

    $container.find('.J_Field').on('blur', () => {
      if (this.inputTip) {
        this.inputTip.hideTip();
      }
      setTimeout(() => {
        this.__hackFocus();
      }, 100);
    });

    $container.find('.csc-upload-button._input, .csc-camera-button._input')
      .off('change.csc')
      .on('change.csc', (e) => {
        this.config.app.util.goldlog('LIVE_CHAT_SEND_PICTURE');
        this.emit('message', [e.target, 'img', null, null]);
      });
    $container.find('.csc-attach-button._input')
      .off('change.csc')
      .on('change.csc', (e) => {
        // this.config.app.util.goldlog('LIVE_CHAT_SEND_FILE');
        this.emit('message', [e.target, 'file', null, null]);
      });

    if (Env.isWebview) {
      $container.find('.csc-camera-button._block')
        .off('click.windvane')
        .on('click.windvane', () => {
          this.config.app.util.goldlog('LIVE_CHAT_SEND_PICTURE');
          windvane.takePhoto((res) => {
            this.emit('message', [res.resourceURL, 'imgUrl', null, null]);
          }, (e) => {
            this.emit('message', [e, 'photoWindvaneFailed', null, null]);
          });
        });

      $container.find('.csc-upload-button._block')
        .off('click.windvane')
        .on('click.windvane', () => {
          this.config.app.util.goldlog('LIVE_CHAT_SEND_PICTURE');
          windvane.uploadPhoto((res) => {
            this.emit('message', [res.resourceURL, 'imgUrl', null, null]);
          }, (e) => {
            this.emit('message', [e, 'photoWindvaneFailed', null, null]);
          });
        });
    }

    this.$pluginTrigger.on(pressEvent, () => {
      this.$pluginsBoard.addClass(ACTIVED_MOD);
    });

    // 快捷短语左右滑的埋点
    let startX = 0;
    this.$shortcuts.on('touchstart', (ev) => {
      startX = ev.touches[0].pageX;
    });
    this.$shortcuts.on('touchend', (ev) => {
      const endX = ev.changedTouches[0].pageX;
      if (endX - startX > 10) {
        this.log.send({
          c: 'shortcut',
          d: 'swiperight',
        });
      } else if (endX - startX < -10) {
        this.log.send({
          c: 'shortcut',
          d: 'swipeleft',
        });
      }
    });

    this.config.app.on(this.config.app.events.DISABLE_UPLOAD, () => {
      this.$container.find('#csc-plugins-popup').remove();
      this.$container.find('.alime-input._plugin-toggle').remove();
    });
  }

  /**
   * 初始化输入类
   * @returns {*}
   * @private
   */
  __initInput() {
    const $container = this.$container;
    const input = new Input({
      log: this.log,
      container: $container.find('#alime-input-form'),
      inputType: this.config.inputType,
      app: this.config.app,
    });
    const $inputField = input.field.$field;
    const $inputForm = $container.find('#alime-input-form');
    this.$inputFormWrapper = $container
                                .find('#alime-input-form-wrapper')
                                .addClass(isPC ? DESKTOP_MOD : MOBILE_MOD);
    this.$pluginsBoard = $container
                          .find('#csc-plugins-popup')
                          .addClass(isPC ? DESKTOP_MOD : MOBILE_MOD);
    // 切换输入方式：语音or文本
    input.on('switch', (ev) => {
      if (ev.inputType === 'keyboard') {
        input.field.focus();
      } else {
        input.field.blur();
      }
      if (!ev.force) {
        this.log.send({
          c: LOG_C,
          d: ev.inputType,
        });
      }
      this.hidePlugin();
      setTimeout(() => {
        this.$inputFormWrapper.height($inputForm.height());
      }, 0);
    });

    // 需要调整body高度
    input.on('resize', () => {
      this.resize();
      this.scrollToBottom();
    });

    $inputField.on('focus', util.buffer(() => {
      if (document.activeElement.tagName === 'TEXTAREA') {
        // 隐藏插件
        this.hidePlugin();
        this.log.send({
          c: LOG_C,
          d: 'focus',
        });
      }
    }, 50));

    // focus & blur event
    $inputField.on('blur', () => {
      this.emit('blur');
    });

    $inputField.on('keyup', () => {
      this.emit('keyup');
    });

    $(window).on('offline.csc', () => {
      this.blockChatAgent();
    }).on('online.csc', () => {
      this.unblockChatAgent();
    });

    return input;
  }

  __convertCustomerMessage(message) {
    return message.replace(/</g, '&lt;').replace(/>/g, '&gt;').trim();
  }

  __sendTextMessage() {
    const f = this.input.field;
    let val = f.getValue() || '';

    val = this.__convertCustomerMessage(val);

    if (val) {
      this.submit(val);
      f.setValue('');
    }
    f.focus();
  }

  /**
   * fix安卓键盘顶起的bug
   * @private
   */
  __hack() {
    const originWinHeight = $(window).height();
    if (util.UA.isAndroid) {
      window.onresize = () => {
        const nowHeight = $(window).height();
        $('body').css({
          bottom: nowHeight - originWinHeight,
          height: nowHeight,
        });
      };
    }
  }

  /**
   * 隐藏加号
   * @return {[type]} [description]
   */
  hidePlus() {
    this.$pluginTrigger.hide();
  }

  /**
   * 添加插件
   * @param config
   *  config.icon 插件对应iconfont
   *  config.title 插件对应标题
   *  config.action 点击事件
   */
  addPlugin(config) {
    const Action = isPC ? 'click' : 'tap';
    const $el = $(`<div class="item ${config.name}" aria-label="${config.title}" role="button">
      <div class="icon-bg">
        <svg class="icon" aria-hidden="true">
          <use xlink:href="#icon-${config.icon}"></use>
        </svg>
      </div>
      <span aria-hidden="true">${config.title}</span>
    </div>`);

    $el.on(Action, (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      config.action.call(this);
    });
    this.$plugin.find('#alime-plugins').append($el);
  }

  /**
   * 隐藏特定插件
   * @param name
   */
  hideTargetPlugin(name) {
    this.$plugin.find(`.${name}`).hide();
  }

  /**
   * 显示特定插件
   * @param name
   */
  showTargetPlugin(name) {
    this.$plugin.find(`.${name}`).show();
  }

  /**
   * 显示插件
   */
  showPlugin() {
    this.input.field.blur();
    this.$plugin.show();
    this.resize();
    this.scrollToBottom();
  }

  /**
   * 隐藏插件
   */
  hidePlugin() {
    this.emit('hidePlugin');
    this.$plugin.hide();
    this.$pluginsBoard.removeClass(ACTIVED_MOD);
    this.resize();
  }

  /**
   * 显示快捷短语
   */
  showShortcuts(forAgent) {
    if (this.config.app.shortcuts && !forAgent) {
      this.config.app.shortcuts.switchToRobot();
    }
    const $shortcutsContent = this.$shortcuts.find('#csc-shortcuts');
    this.$shortcuts.show();
    // 将快捷短语更新到最左
    $shortcutsContent.scrollLeft(0);
    setTimeout(() => {
      this.resize();
    }, 500);
  }
  /**
   * 隐藏快捷短语
   */
  hideShortcuts() {
    this.$shortcuts.hide();
    this.resize();
  }

  showEndConversationHolder() {
    this.$endConversationHolder.removeClass('-hidden');
  }

  hideEndConversationHolder() {
    this.$endConversationHolder.addClass('-hidden');
  }

  /**
   * 重新设置body高度和top bottom
   */
  resize() {
    this.emit('resize');
  }
  /**
   * 需要body滚到最底部
   */
  scrollToBottom() {
    this.emit('scrollToBottom');
  }

  __scrollIntoView() {
    const form = this.$container.find('#alime-input-form')[0];
    setTimeout(() => {
      form.scrollIntoView();
    }, 300);
  }

  blockChatAgent() {
    this.$container.addClass(BLOCKED_MOD);
    this.$inputFormWrapper.addClass(BLOCKED_MOD);
    this.$pluginTrigger.addClass(BLOCKED_MOD);
    // this.$container.find('#alime-text-input').blur();
    // console.log(this.$container.find('#alime-text-input'));
  }

  unblockChatAgent() {
    this.$pluginsBoard.removeClass(ACTIVED_MOD);
    this.$container.removeClass(BLOCKED_MOD);
    this.$inputFormWrapper.removeClass(BLOCKED_MOD);
    this.$pluginTrigger.removeClass(BLOCKED_MOD);
  }

  switchRole(object) {
    switch (object) {
      case AGENT:
        // change footer UI to chat with agent
        // hide shortcuts, emoji, show plugin trigger button on mobile
        this.$shortcuts.hide();
        this.$pluginsBoard.show();
        this.$container.addClass(AGENT_MOD);
        this.$inputFormWrapper.addClass(AGENT_MOD);
        this.input.$container.parent().css('height', '');
        this.resize();
        break;
      default:
        // change footer UI to chat with robot
        this.$shortcuts.show();
        this.$pluginsBoard.hide();
        this.$container.removeClass(AGENT_MOD);
        this.$inputFormWrapper.removeClass(AGENT_MOD);
        this.resize();
    }
  }
}



// WEBPACK FOOTER //
// ./src/component/view/footer/index.js